import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { translations } from "./translations";
import { MapPin, Clock, Phone, Mail, ChevronDown, ArrowRight, Sparkles, Heart, Star } from "lucide-react";

// Assets
const ASSETS = {
  logo: "https://customer-assets.emergentagent.com/job_artisan-gelato-paris/artifacts/knl7hpl2_Capture%20d%E2%80%99e%CC%81cran%202026-03-19%20a%CC%80%2014.06.48.png",
  heroTexture: "https://customer-assets.emergentagent.com/job_78420609-4052-4bfd-b2c3-71a7a1969836/artifacts/qog8hini_IMG_0898.jpg",
  pistachioBowl: "https://customer-assets.emergentagent.com/job_78420609-4052-4bfd-b2c3-71a7a1969836/artifacts/3to4osum_IMG_0890.jpg",
  coneServing: "https://customer-assets.emergentagent.com/job_78420609-4052-4bfd-b2c3-71a7a1969836/artifacts/emkwa4et_IMG_0895.jpg",
  pistachioCone: "https://customer-assets.emergentagent.com/job_78420609-4052-4bfd-b2c3-71a7a1969836/artifacts/va4tdpxx_IMG_0897.jpg",
  vanillaCaramel: "https://customer-assets.emergentagent.com/job_78420609-4052-4bfd-b2c3-71a7a1969836/artifacts/8oorewlv_IMG_0894.jpg",
  brandedCupChocolate: "https://customer-assets.emergentagent.com/job_artisan-gelato-paris/artifacts/ey3u1z65_Capture%20d%E2%80%99e%CC%81cran%202026-03-19%20a%CC%80%2014.05.51.png",
  shopFront: "https://customer-assets.emergentagent.com/job_artisan-gelato-paris/artifacts/1by8he5b_Capture%20d%E2%80%99e%CC%81cran%202026-03-19%20a%CC%80%2014.06.10.png",
  brandedCupFruit: "https://customer-assets.emergentagent.com/job_artisan-gelato-paris/artifacts/e59fal1i_Capture%20d%E2%80%99e%CC%81cran%202026-03-19%20a%CC%80%2014.07.08.png",
  chocolateTexture: "https://customer-assets.emergentagent.com/job_artisan-gelato-paris/artifacts/t87vqmi5_IMG_0892.jpg",
  waffleCones: "https://customer-assets.emergentagent.com/job_artisan-gelato-paris/artifacts/l40p0edi_IMG_0899.jpg",
  mary: "https://customer-assets.emergentagent.com/job_artisan-gelato-paris/artifacts/nrzqo5f7_Capture%20d%E2%80%99e%CC%81cran%202026-03-19%20a%CC%80%2014.23.18.png",
  coneAction: "https://customer-assets.emergentagent.com/job_artisan-gelato-paris/artifacts/9gv0hd5i_Capture%20d%E2%80%99e%CC%81cran%202026-03-27%20a%CC%80%2013.31.51.png",
  shopPastelli: "https://customer-assets.emergentagent.com/job_artisan-gelato-paris/artifacts/vu5driwh_Capture%20d%E2%80%99e%CC%81cran%202026-03-27%20a%CC%80%2013.32.52.png",
  interiorPink: "https://customer-assets.emergentagent.com/job_artisan-gelato-paris/artifacts/dj6k0ea1_Capture%20d%E2%80%99e%CC%81cran%202026-03-27%20a%CC%80%2013.36.25.png",
  // Neighborhood photos
  maraisStreet: "https://images.unsplash.com/photo-1729551539884-9ce692713022?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxMZSUyME1hcmFpcyUyMFBhcmlzJTIwc3RyZWV0JTIwY29sb3JmdWx8ZW58MHx8fHwxNzc0NjE1NDcwfDA&ixlib=rb-4.1.0&q=85",
  parisCafe: "https://images.unsplash.com/photo-1583874531437-36948c2714ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxQYXJpcyUyMGNhZmUlMjB0ZXJyYWNlJTIwc3VtbWVyfGVufDB8fHx8MTc3NDYxNTQ3Mnww&ixlib=rb-4.1.0&q=85",
  parisBuilding: "https://images.unsplash.com/photo-1766847733852-dc8a272d1e6b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxQYXJpcyUyMGNhZmUlMjB0ZXJyYWNlJTIwc3VtbWVyfGVufDB8fHx8MTc3NDYxNTQ3Mnww&ixlib=rb-4.1.0&q=85",
};

// Hook for scroll animations
const useScrollAnimation = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

// Navigation Component
const Navigation = ({ lang, setLang, t }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-4 left-4 right-4 z-50 nav-floating rounded-full px-4 md:px-6 py-2 md:py-3 flex justify-between items-center transition-all duration-300 ${
        scrolled ? "shadow-md" : "shadow-sm"
      }`}
    >
      <a href="#hero" className="flex items-center" data-testid="nav-logo">
        <span className="font-playfair text-xl md:text-2xl font-medium" style={{ color: "#8B3A7D" }}>Mary</span>
      </a>
      
      <div className="hidden md:flex items-center gap-8">
        <a href="#flavors" className="text-sm font-medium text-stone-600 hover:text-stone-900 transition-all duration-300 hover:scale-105" data-testid="nav-flavors">
          {t.nav.flavors}
        </a>
        <a href="#story" className="text-sm font-medium text-stone-600 hover:text-stone-900 transition-all duration-300 hover:scale-105" data-testid="nav-story">
          {t.nav.story}
        </a>
        <a href="#location" className="text-sm font-medium text-stone-600 hover:text-stone-900 transition-all duration-300 hover:scale-105" data-testid="nav-location">
          {t.nav.visit}
        </a>
      </div>

      <div className="lang-toggle" data-testid="language-toggle">
        <button
          onClick={() => setLang("fr")}
          className={lang === "fr" ? "active" : ""}
          data-testid="lang-fr"
        >
          FR
        </button>
        <span className="text-stone-300">|</span>
        <button
          onClick={() => setLang("en")}
          className={lang === "en" ? "active" : ""}
          data-testid="lang-en"
        >
          EN
        </button>
        <span className="text-stone-300">|</span>
        <button
          onClick={() => setLang("it")}
          className={lang === "it" ? "active" : ""}
          data-testid="lang-it"
        >
          IT
        </button>
      </div>
    </nav>
  );
};

// Hero Section - With texture background
const HeroSection = ({ t }) => {
  return (
    <section id="hero" className="relative min-h-screen flex items-center" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src={ASSETS.heroTexture}
          alt="Artisanal gelato texture"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-white/40"></div>
      </div>

      {/* Content */}
      <div className="container-custom relative z-10 pt-24">
        <div className="max-w-2xl">
          {/* Award Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 animate-fadeInUp opacity-0" style={{ animationDelay: "0.1s", animationFillMode: "forwards", backgroundColor: "#E8D5E4" }}>
            <span className="text-sm font-medium" style={{ color: "#8B3A7D" }}>
              {t.hero.award}
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-playfair font-medium text-stone-900 mb-4 animate-fadeInUp opacity-0 leading-tight" style={{ animationDelay: "0.2s", animationFillMode: "forwards" }} data-testid="hero-title">
            {t.hero.subtitle}
          </h1>
          
          <p className="body-text mb-8 animate-fadeInUp opacity-0" style={{ animationDelay: "0.3s", animationFillMode: "forwards" }}>
            {t.hero.tagline}
          </p>
          
          <a
            href="#location"
            className="btn-primary inline-flex items-center gap-3 animate-fadeInUp opacity-0"
            style={{ animationDelay: "0.4s", animationFillMode: "forwards" }}
            data-testid="hero-cta"
          >
            {t.cta.button}
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 scroll-indicator hidden md:block">
        <ChevronDown className="w-6 h-6 text-stone-400" />
      </div>
    </section>
  );
};

// Flavors Section - Full images without cropping
const FlavorsSection = ({ t }) => {
  const [ref, isVisible] = useScrollAnimation();
  
  const images = [
    { src: ASSETS.brandedCupChocolate, alt: "Mary Gelateria cup with chocolate and vanilla" },
    { src: ASSETS.pistachioBowl, alt: "Pistachio gelato" },
    { src: ASSETS.brandedCupFruit, alt: "Mary Gelateria cup with fruit sorbets" },
    { src: ASSETS.chocolateTexture, alt: "Chocolate gelato texture" },
    { src: ASSETS.vanillaCaramel, alt: "Vanilla caramel gelato" },
    { src: ASSETS.coneAction, alt: "Fresh gelato cone" },
  ];

  return (
    <section id="flavors" className="section-padding bg-[#FAFAF9] overflow-hidden" data-testid="flavors-section">
      <div className="container-custom" ref={ref}>
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ backgroundColor: "#E8D5E4" }}>
            <Sparkles className="w-4 h-4" style={{ color: "#8B3A7D" }} />
            <span className="text-sm font-medium" style={{ color: "#8B3A7D" }}>{t.flavors.newFlavors}</span>
            <Sparkles className="w-4 h-4" style={{ color: "#8B3A7D" }} />
          </div>
          <h2 className="heading-lg text-stone-900 mb-4" data-testid="flavors-title">
            {t.flavors.sectionTitle}
          </h2>
          <p className="body-text max-w-xl mx-auto">
            {t.flavors.noAdditives}
          </p>
        </div>

        {/* Image Gallery - Natural sizes */}
        <div className="columns-2 md:columns-3 gap-4 space-y-4">
          {images.map((image, index) => (
            <div
              key={index}
              className={`break-inside-avoid rounded-2xl overflow-hidden group cursor-pointer hover-lift transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
              data-testid={`gelato-image-${index}`}
            >
              <div className="relative">
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-auto transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                  <Heart className="w-6 h-6 text-white animate-pulse" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Badge */}
        <div className={`mt-12 text-center transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-flex items-center gap-2 px-6 py-3 bg-white rounded-full text-sm font-medium shadow-lg border border-stone-100 hover-lift cursor-default" style={{ color: "#8B3A7D" }}>
            <Star className="w-4 h-4 fill-current" />
            {t.flavors.pureIngredients}
            <Star className="w-4 h-4 fill-current" />
          </span>
        </div>
      </div>
    </section>
  );
};

// Story Section
const StorySection = ({ t }) => {
  const [ref, isVisible] = useScrollAnimation();

  return (
    <section id="story" className="section-padding bg-white overflow-hidden" data-testid="story-section">
      <div className="container-custom" ref={ref}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Images - Mary photo */}
          <div className={`relative transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-20'}`}>
            <div className="rounded-2xl overflow-hidden hover-lift">
              <img
                src={ASSETS.mary}
                alt="Mary - fondatrice de Mary Gelateria"
                className="w-full h-auto transition-transform duration-700 hover:scale-105"
                loading="lazy"
              />
            </div>
            {/* Floating card with animation */}
            <div className="absolute -bottom-4 right-4 md:right-8 rounded-2xl px-5 py-4 shadow-xl text-center animate-float" style={{ backgroundColor: "#E8D5E4" }}>
              <p className="font-italiana text-3xl md:text-4xl leading-tight" style={{ color: "#8B3A7D" }}>
                {t.story.highlight}
              </p>
              <p className="text-xs mt-1 font-medium" style={{ color: "#8B3A7D" }}>
                {t.story.highlightText}
              </p>
            </div>
          </div>

          {/* Content */}
          <div className={`lg:pl-8 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20'}`}>
            <p className="caption-text mb-4" style={{ color: "#8B3A7D" }}>{t.story.subtitle}</p>
            <h2 className="heading-lg text-stone-900 mb-8" data-testid="story-title">
              {t.story.sectionTitle}
            </h2>
            
            <div className="space-y-6">
              <p className="body-text">
                {t.story.paragraph1}
              </p>
              <p className="body-text">
                {t.story.paragraph2}
              </p>
              <p className="body-text font-medium text-stone-800">
                {t.story.paragraph3}
              </p>
            </div>
          </div>
        </div>

        {/* Additional images - natural sizes in masonry */}
        <div className="mt-16 columns-2 md:columns-4 gap-4 space-y-4">
          {[
            { src: ASSETS.shopPastelli, alt: "Mary Gelateria Pastelli storefront" },
            { src: ASSETS.interiorPink, alt: "Mary Gelateria interior" },
            { src: ASSETS.coneAction, alt: "Gelato cone being served" },
            { src: ASSETS.waffleCones, alt: "Fresh waffle cones" },
          ].map((item, index) => (
            <div 
              key={index}
              className={`break-inside-avoid rounded-2xl overflow-hidden hover-lift transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${500 + index * 100}ms` }}
            >
              <img
                src={item.src}
                alt={item.alt}
                className="w-full h-auto transition-transform duration-500 hover:scale-105"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Neighborhood Section - Clean banner style
const NeighborhoodSection = ({ t }) => {
  const [ref, isVisible] = useScrollAnimation();

  return (
    <section className="py-20 md:py-28 overflow-hidden relative" data-testid="neighborhood-section">
      {/* Background image with overlay */}
      <div className="absolute inset-0">
        <img
          src={ASSETS.maraisStreet}
          alt="Le Marais Paris"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-white/90"></div>
      </div>
      
      <div className="container-custom relative z-10" ref={ref}>
        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="caption-text mb-4" style={{ color: "#8B3A7D" }}>{t.neighborhood.subtitle}</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-playfair text-stone-900 mb-6">
            {t.neighborhood.sectionTitle}
          </h2>
          <p className="body-text text-stone-600 mb-8">
            {t.neighborhood.description}
          </p>
          
          {/* Simple tags */}
          <div className="flex flex-wrap justify-center gap-3">
            <span className="px-4 py-2 rounded-full text-sm font-medium border" style={{ borderColor: "#8B3A7D", color: "#8B3A7D" }}>
              {t.neighborhood.feature1}
            </span>
            <span className="px-4 py-2 rounded-full text-sm font-medium border" style={{ borderColor: "#8B3A7D", color: "#8B3A7D" }}>
              {t.neighborhood.feature2}
            </span>
            <span className="px-4 py-2 rounded-full text-sm font-medium border" style={{ borderColor: "#8B3A7D", color: "#8B3A7D" }}>
              {t.neighborhood.feature3}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

// Location Section
const LocationSection = ({ t }) => {
  const mapSrc = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2624.8!2d2.3545!3d48.8634!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e66e1c8b8c5c5b%3A0x0!2s60%20Rue%20du%20Temple%2C%2075003%20Paris!5e0!3m2!1sen!2sfr!4v1";
  const tripAdvisorUrl = "https://www.tripadvisor.fr/Restaurant_Review-g187147-d3524446-Reviews-Mary_Gelateria-Paris_Ile_de_France.html";

  return (
    <section id="location" className="section-padding bg-[#F9F5F8]" data-testid="location-section">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="caption-text mb-4" style={{ color: "#8B3A7D" }}>{t.location.subtitle}</p>
          <h2 className="heading-lg text-stone-900" data-testid="location-title">
            {t.location.sectionTitle}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Map */}
          <div className="map-container aspect-[4/3] lg:aspect-auto lg:min-h-[400px]">
            <iframe
              src={mapSrc}
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: "100%" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Mary Gelateria Location"
              data-testid="location-map"
            ></iframe>
          </div>

          {/* Info Card */}
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm">
            <h3 className="font-playfair text-2xl mb-8" style={{ color: "#8B3A7D" }}>Mary Gelateria</h3>
            
            <div className="space-y-6">
              {/* Address */}
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: "#8B3A7D" }} strokeWidth={1.5} />
                <div>
                  <p className="font-medium text-stone-900">{t.location.address}</p>
                  <p className="text-stone-600">{t.location.city}</p>
                </div>
              </div>

              {/* Hours */}
              <div className="flex items-start gap-4">
                <Clock className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: "#8B3A7D" }} strokeWidth={1.5} />
                <div>
                  <p className="font-medium text-stone-900 mb-1">{t.location.hours}</p>
                  <p className="text-stone-600">{t.location.schedule.weekdays}</p>
                  <p className="text-stone-500">{t.location.schedule.closed}</p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-4">
                <Phone className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: "#8B3A7D" }} strokeWidth={1.5} />
                <div>
                  <a href="tel:+33173748822" className="text-stone-900 hover:text-stone-600 transition-colors">
                    {t.location.phone}
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4">
                <Mail className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: "#8B3A7D" }} strokeWidth={1.5} />
                <div>
                  <a href="mailto:mary.gelateria@gmail.com" className="text-stone-900 hover:text-stone-600 transition-colors">
                    {t.location.email}
                  </a>
                </div>
              </div>

              {/* TripAdvisor */}
              <div className="pt-4 border-t border-stone-100">
                <a
                  href={tripAdvisorUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium transition-colors hover:opacity-80"
                  style={{ color: "#8B3A7D" }}
                  data-testid="tripadvisor-link"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                  </svg>
                  {t.location.reviews}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* CTA */}
            <a
              href="https://maps.google.com/?q=60+rue+du+Temple+75003+Paris"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-3 mt-8 w-full justify-center"
              data-testid="location-directions-btn"
            >
              {t.cta.button}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = ({ t }) => {
  return (
    <section className="section-padding" style={{ backgroundColor: "#8B3A7D" }} data-testid="cta-section">
      <div className="container-custom text-center">
        <p className="caption-text text-white/70 mb-4">{t.cta.subtitle}</p>
        <h2 className="heading-lg text-white mb-6" data-testid="cta-title">
          {t.cta.title}
        </h2>
        <p className="body-text text-white/80 max-w-xl mx-auto mb-10">
          {t.cta.description}
        </p>
        <a
          href="#location"
          className="inline-flex items-center gap-3 rounded-full px-8 py-4 text-sm tracking-widest uppercase transition-all duration-300 bg-white hover:bg-stone-100 hover:scale-105 active:scale-95"
          style={{ color: "#8B3A7D" }}
          data-testid="cta-button"
        >
          {t.nav.visit}
          <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </section>
  );
};

// Footer
const Footer = ({ t }) => {
  return (
    <footer className="py-12 bg-[#FAFAF9] border-t border-stone-100" data-testid="footer">
      <div className="container-custom">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-center md:text-left">
            <p className="font-playfair text-xl font-medium" style={{ color: "#8B3A7D" }}>Mary Gelateria</p>
            <p className="text-sm text-stone-500 mt-1">{t.footer.tagline} · {t.footer.location}</p>
          </div>
          <p className="text-sm text-stone-400">
            {t.footer.copyright}
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App
function App() {
  const [lang, setLang] = useState("fr");
  const t = translations[lang];

  // Update HTML lang attribute
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Update meta tags for SEO
  useEffect(() => {
    const title = lang === "fr" 
      ? "Mary Gelateria | Meilleures Glaces Artisanales du Marais, Paris"
      : "Mary Gelateria | Best Ice Cream in Le Marais, Paris";
    const description = lang === "fr"
      ? "Découvrez Mary Gelateria, glacier artisanal au cœur du Marais à Paris. Glaces 100% fruits, préparées fraîches chaque matin. Sans additifs, sans arômes artificiels."
      : "Discover Mary Gelateria, artisanal gelato shop in the heart of Le Marais, Paris. 100% fruit-based ice cream, freshly made every morning. No additives, no artificial flavors.";

    document.title = title;
    
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = description;
  }, [lang]);

  return (
    <div className="App bg-[#FAFAF9]" data-testid="app-container">
      <Navigation lang={lang} setLang={setLang} t={t} />
      
      <main>
        <HeroSection t={t} />
        <FlavorsSection t={t} />
        <StorySection t={t} />
        <NeighborhoodSection t={t} />
        <LocationSection t={t} />
        <CTASection t={t} />
      </main>
      
      <Footer t={t} />
    </div>
  );
}

export default App;
